import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Badge,
  Avatar,
  InputAdornment,
  Collapse,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent
} from '@mui/lab';
import {
  CalendarMonth as SeasonIcon,
  Description as ContractIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Timer as PendingIcon,
  Cancel as CancelIcon,
  Refresh as RenewIcon,
  Gavel as TerminateIcon,
  Email as EmailIcon,
  Print as PrintIcon,
  Note as NoteIcon,
  Alarm as AlertIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Event as EventIcon,
  Home as SiteIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Assignment as TaskIcon,
  Notifications as NotificationIcon,
  Assessment as ReportIcon,
  Schedule as ScheduleIcon,
  CreditCard as CreditIcon,
  AccountBalance as BankIcon,
  TrendingUp as TrendingIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`seasonal-tabpanel-${index}`}
      aria-labelledby={`seasonal-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SeasonalPage: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [seasons, setSeasons] = useState<any[]>([]);
  const [contracts, setContracts] = useState<any[]>([]);
  const [sites, setSites] = useState<any[]>([]);
  const [guests, setGuests] = useState<any[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<any>(null);
  const [selectedContract, setSelectedContract] = useState<any>(null);
  const [dashboardData, setDashboardData] = useState<any>({});
  
  // UI states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [contractFilter, setContractFilter] = useState({
    status: 'all',
    type: 'all',
    year: new Date().getFullYear()
  });
  const [seasonFilter, setSeasonFilter] = useState({
    year: new Date().getFullYear(),
    status: 'all'
  });
  const [expandedContract, setExpandedContract] = useState<string | null>(null);
  
  // Dialog states
  const [seasonDialog, setSeasonDialog] = useState(false);
  const [contractDialog, setContractDialog] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [violationDialog, setViolationDialog] = useState(false);
  const [renewalDialog, setRenewalDialog] = useState(false);
  const [terminationDialog, setTerminationDialog] = useState(false);
  
  // Form states
  const [seasonForm, setSeasonForm] = useState({
    name: '',
    year: new Date().getFullYear(),
    period: {
      startDate: null as any,
      endDate: null as any
    },
    pricing: {
      baseRate: {
        seasonal: '',
        monthly: '',
        weekly: ''
      }
    },
    policies: {
      renewal: {
        priority: 'returning_guests',
        deadline: null as any
      }
    }
  });
  
  const [contractForm, setContractForm] = useState({
    guest: null as any,
    site: null as any,
    type: 'seasonal',
    season: {
      name: '',
      year: new Date().getFullYear()
    },
    term: {
      startDate: null as any,
      endDate: null as any
    },
    pricing: {
      totalAmount: '',
      paymentSchedule: 'lump_sum'
    }
  });
  
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    method: 'cash',
    reference: ''
  });

  useEffect(() => {
    fetchData();
  }, [contractFilter, seasonFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchSeasons(),
        fetchContracts(),
        fetchDashboardData(),
        fetchSites(),
        fetchGuests()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSeasons = async () => {
    try {
      const params: any = {};
      if (seasonFilter.year) params.year = seasonFilter.year;
      if (seasonFilter.status !== 'all') params.status = seasonFilter.status;
      
      const response = await api.get('/seasonal/seasons', { params });
      setSeasons(response.data);
    } catch (error) {
      console.error('Error fetching seasons:', error);
    }
  };

  const fetchContracts = async () => {
    try {
      const params: any = { 
        page: page + 1, 
        limit: rowsPerPage,
        year: contractFilter.year
      };
      if (contractFilter.status !== 'all') params.status = contractFilter.status;
      if (contractFilter.type !== 'all') params.type = contractFilter.type;
      
      const response = await api.get('/seasonal/contracts', { params });
      setContracts(response.data.contracts);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const [
        activeResponse,
        expiringResponse,
        overdueResponse,
        occupancyResponse
      ] = await Promise.all([
        api.get('/seasonal/contracts/active'),
        api.get('/seasonal/contracts/expiring/30'),
        api.get('/seasonal/contracts/overdue-payments'),
        api.get(`/seasonal/reports/site-occupancy/${new Date().getFullYear()}`)
      ]);
      
      setDashboardData({
        activeContracts: activeResponse.data.length,
        expiringContracts: expiringResponse.data.length,
        overduePayments: overdueResponse.data.length,
        occupancyData: occupancyResponse.data
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await api.get('/sites');
      setSites(response.data);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  const fetchGuests = async () => {
    try {
      const response = await api.get('/guests');
      setGuests(response.data);
    } catch (error) {
      console.error('Error fetching guests:', error);
    }
  };

  const handleCreateSeason = async () => {
    try {
      await api.post(
        `/seasonal/seasons`,
        seasonForm      );
      
      setSeasonDialog(false);
      fetchData();
      alert('Season created successfully!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error creating season');
    }
  };

  const handleCreateContract = async () => {
    try {
      await api.post(
        `/seasonal/contracts`,
        {
          ...contractForm,
          pricing: {
            ...contractForm.pricing,
            totalAmount: parseFloat(contractForm.pricing.totalAmount)
          }
        }      );
      
      setContractDialog(false);
      fetchData();
      alert('Contract created successfully!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error creating contract');
    }
  };

  const handleProcessPayment = async (contractId: string, installmentId: string) => {
    try {
      await api.post(
        `/seasonal/contracts/${contractId}/payments/${installmentId}`,
        {
          ...paymentForm,
          amount: parseFloat(paymentForm.amount)
        }      );
      
      setPaymentDialog(false);
      fetchData();
      alert('Payment processed successfully!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error processing payment');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: any = {
      'active': 'success',
      'pending_approval': 'warning',
      'suspended': 'error',
      'terminated': 'error',
      'expired': 'default',
      'renewed': 'info',
      'draft': 'default'
    };
    return colors[status] || 'default';
  };

  const getTypeColor = (type: string) => {
    const colors: any = {
      'seasonal': 'primary',
      'annual': 'success',
      'monthly': 'info',
      'weekly_extended': 'secondary'
    };
    return colors[type] || 'default';
  };

  const renderMetricCard = (title: string, value: any, subtitle?: string, icon?: React.ReactNode, color?: string) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {icon}
          <Typography color="text.secondary" variant="subtitle2" sx={{ ml: 1 }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" color={color}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const renderContractTimeline = (contract: any) => (
    <Timeline position="alternate">
      <TimelineItem>
        <TimelineSeparator>
          <TimelineDot color="primary">
            <ContractIcon />
          </TimelineDot>
          <TimelineConnector />
        </TimelineSeparator>
        <TimelineContent>
          <Typography variant="body2">
            Contract Created
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {dayjs(contract.createdAt).format('MMM D, YYYY')}
          </Typography>
        </TimelineContent>
      </TimelineItem>
      
      {contract.approvals.map((approval: any, index: number) => (
        <TimelineItem key={index}>
          <TimelineSeparator>
            <TimelineDot color={approval.status === 'approved' ? 'success' : 'warning'}>
              <CheckIcon />
            </TimelineDot>
            <TimelineConnector />
          </TimelineSeparator>
          <TimelineContent>
            <Typography variant="body2">
              {approval.status === 'approved' ? 'Approved' : 'Pending Approval'}
            </Typography>
            <Typography variant="caption">
              {approval.notes}
            </Typography>
          </TimelineContent>
        </TimelineItem>
      ))}
      
      {contract.violations.map((violation: any, index: number) => (
        <TimelineItem key={index}>
          <TimelineSeparator>
            <TimelineDot color="error">
              <WarningIcon />
            </TimelineDot>
            {index < contract.violations.length - 1 && <TimelineConnector />}
          </TimelineSeparator>
          <TimelineContent>
            <Typography variant="body2">
              Violation: {violation.type}
            </Typography>
            <Typography variant="caption">
              {violation.description}
            </Typography>
          </TimelineContent>
        </TimelineItem>
      ))}
    </Timeline>
  );

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Seasonal Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage seasonal contracts, long-term leases, and extended stays
        </Typography>
      </Box>

      {/* Dashboard Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Active Contracts',
            dashboardData.activeContracts || 0,
            'Currently active',
            <ContractIcon color="primary" />
          )}
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Expiring Soon',
            dashboardData.expiringContracts || 0,
            'Next 30 days',
            <WarningIcon color="warning" />,
            'warning.main'
          )}
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Overdue Payments',
            dashboardData.overduePayments || 0,
            'Need attention',
            <AlertIcon color="error" />,
            'error.main'
          )}
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Total Revenue',
            `$${(dashboardData.occupancyData?.reduce((sum: number, site: any) => sum + site.totalRevenue, 0) || 0).toLocaleString()}`,
            'This year',
            <TrendingIcon color="success" />
          )}
        </Grid>
      </Grid>

      <Paper>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Dashboard" icon={<SeasonIcon />} iconPosition="start" />
          <Tab label="Seasons" icon={<EventIcon />} iconPosition="start" />
          <Tab label="Contracts" icon={<ContractIcon />} iconPosition="start" />
          <Tab label="Reports" icon={<ReportIcon />} iconPosition="start" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {/* Dashboard Tab */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Contract Status Overview
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <Badge badgeContent={dashboardData.activeContracts} color="success">
                          <CheckIcon />
                        </Badge>
                      </ListItemIcon>
                      <ListItemText primary="Active Contracts" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Badge badgeContent={dashboardData.expiringContracts} color="warning">
                          <ScheduleIcon />
                        </Badge>
                      </ListItemIcon>
                      <ListItemText primary="Expiring Soon" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Badge badgeContent={dashboardData.overduePayments} color="error">
                          <MoneyIcon />
                        </Badge>
                      </ListItemIcon>
                      <ListItemText primary="Overdue Payments" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Quick Actions
                  </Typography>
                  <Stack spacing={2}>
                    <Button
                      variant="outlined"
                      startIcon={<ContractIcon />}
                      onClick={() => setContractDialog(true)}
                      fullWidth
                    >
                      Create New Contract
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<SeasonIcon />}
                      onClick={() => setSeasonDialog(true)}
                      fullWidth
                    >
                      Create New Season
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<NotificationIcon />}
                      fullWidth
                    >
                      Send Renewal Notices
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<ReportIcon />}
                      fullWidth
                    >
                      Generate Report
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Seasons Tab */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={2}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Year</InputLabel>
                  <Select
                    value={seasonFilter.year}
                    onChange={(e) => setSeasonFilter({ ...seasonFilter, year: Number(e.target.value) })}
                    label="Year"
                  >
                    {[2023, 2024, 2025, 2026].map(year => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={seasonFilter.status}
                    onChange={(e) => setSeasonFilter({ ...seasonFilter, status: e.target.value })}
                    label="Status"
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="planning">Planning</MenuItem>
                    <MenuItem value="registration_open">Registration Open</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              
              <Button
                variant="contained"
                startIcon={<SeasonIcon />}
                onClick={() => setSeasonDialog(true)}
              >
                New Season
              </Button>
            </Stack>
          </Box>

          <Grid container spacing={3}>
            {seasons.map((season) => (
              <Grid item xs={12} md={6} lg={4} key={season._id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6">
                        {season.name} {season.year}
                      </Typography>
                      <Chip
                        label={season.status}
                        size="small"
                        color={season.status === 'active' ? 'success' : 'default'}
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {dayjs(season.period.startDate).format('MMM D')} - {dayjs(season.period.endDate).format('MMM D, YYYY')}
                    </Typography>
                    
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        Occupancy: {season.analytics?.occupancyRate?.toFixed(1) || 0}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={season.analytics?.occupancyRate || 0}
                        sx={{ mt: 1, mb: 2 }}
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary">
                      Revenue: ${(season.analytics?.revenue?.total || 0).toLocaleString()}
                    </Typography>
                  </CardContent>
                  
                  <CardActions>
                    <Button size="small" onClick={() => setSelectedSeason(season)}>
                      View Details
                    </Button>
                    <IconButton size="small">
                      <EmailIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* Contracts Tab */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={2}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Year</InputLabel>
                  <Select
                    value={contractFilter.year}
                    onChange={(e) => setContractFilter({ ...contractFilter, year: Number(e.target.value) })}
                    label="Year"
                  >
                    {[2023, 2024, 2025, 2026].map(year => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={contractFilter.status}
                    onChange={(e) => setContractFilter({ ...contractFilter, status: e.target.value })}
                    label="Status"
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="pending_approval">Pending</MenuItem>
                    <MenuItem value="suspended">Suspended</MenuItem>
                    <MenuItem value="terminated">Terminated</MenuItem>
                    <MenuItem value="expired">Expired</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={contractFilter.type}
                    onChange={(e) => setContractFilter({ ...contractFilter, type: e.target.value })}
                    label="Type"
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="seasonal">Seasonal</MenuItem>
                    <MenuItem value="annual">Annual</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="weekly_extended">Weekly Extended</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              
              <Button
                variant="contained"
                startIcon={<ContractIcon />}
                onClick={() => setContractDialog(true)}
              >
                New Contract
              </Button>
            </Stack>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Contract ID</TableCell>
                  <TableCell>Guest</TableCell>
                  <TableCell>Site</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Term</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contracts.map((contract) => (
                  <React.Fragment key={contract._id}>
                    <TableRow>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => setExpandedContract(
                            expandedContract === contract._id ? null : contract._id
                          )}
                        >
                          {expandedContract === contract._id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{contract.contractId}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {contract.guest?.firstName} {contract.guest?.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {contract.guest?.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            Site {contract.site?.number}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {contract.site?.type}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={contract.type}
                          size="small"
                          color={getTypeColor(contract.type)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {dayjs(contract.term.startDate).format('MM/DD/YY')} - {dayjs(contract.term.endDate).format('MM/DD/YY')}
                        </Typography>
                      </TableCell>
                      <TableCell>${contract.pricing.totalAmount.toLocaleString()}</TableCell>
                      <TableCell>
                        <Chip
                          label={contract.status}
                          size="small"
                          color={getStatusColor(contract.status)}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Process Payment">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedContract(contract);
                                setPaymentDialog(true);
                              }}
                            >
                              <PaymentIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Document">
                            <IconButton size="small">
                              <PrintIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Add Note">
                            <IconButton size="small">
                              <NoteIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={9} sx={{ py: 0 }}>
                        <Collapse in={expandedContract === contract._id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 3 }}>
                            <Grid container spacing={3}>
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Contract Timeline
                                </Typography>
                                {renderContractTimeline(contract)}
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Payment Schedule
                                </Typography>
                                <List dense>
                                  {contract.pricing.installments?.map((installment: any, index: number) => (
                                    <ListItem key={index}>
                                      <ListItemIcon>
                                        {installment.paid?.status ? (
                                          <CheckIcon color="success" />
                                        ) : (
                                          <PendingIcon color="warning" />
                                        )}
                                      </ListItemIcon>
                                      <ListItemText
                                        primary={`$${installment.amount.toFixed(2)}`}
                                        secondary={`Due: ${dayjs(installment.dueDate).format('MM/DD/YYYY')}`}
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              </Grid>
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
            
            <TablePagination
              component="div"
              count={100} // This should come from API
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {/* Reports Tab */}
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <ReportIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Seasonal Reports
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Advanced reporting and analytics for seasonal operations
            </Typography>
          </Box>
        </TabPanel>
      </Paper>

      {/* Create Season Dialog */}
      <Dialog open={seasonDialog} onClose={() => setSeasonDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Season</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Season Name"
              value={seasonForm.name}
              onChange={(e) => setSeasonForm({ ...seasonForm, name: e.target.value })}
              fullWidth
              required
            />
            
            <TextField
              label="Year"
              type="number"
              value={seasonForm.year}
              onChange={(e) => setSeasonForm({ ...seasonForm, year: parseInt(e.target.value) })}
              fullWidth
              required
            />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <DatePicker
                  label="Start Date"
                  value={seasonForm.period.startDate}
                  onChange={(date) => setSeasonForm({
                    ...seasonForm,
                    period: { ...seasonForm.period, startDate: date }
                  })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  label="End Date"
                  value={seasonForm.period.endDate}
                  onChange={(date) => setSeasonForm({
                    ...seasonForm,
                    period: { ...seasonForm.period, endDate: date }
                  })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </Grid>
            
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  label="Seasonal Rate"
                  type="number"
                  value={seasonForm.pricing.baseRate.seasonal}
                  onChange={(e) => setSeasonForm({
                    ...seasonForm,
                    pricing: {
                      ...seasonForm.pricing,
                      baseRate: { ...seasonForm.pricing.baseRate, seasonal: e.target.value }
                    }
                  })}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Monthly Rate"
                  type="number"
                  value={seasonForm.pricing.baseRate.monthly}
                  onChange={(e) => setSeasonForm({
                    ...seasonForm,
                    pricing: {
                      ...seasonForm.pricing,
                      baseRate: { ...seasonForm.pricing.baseRate, monthly: e.target.value }
                    }
                  })}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Weekly Rate"
                  type="number"
                  value={seasonForm.pricing.baseRate.weekly}
                  onChange={(e) => setSeasonForm({
                    ...seasonForm,
                    pricing: {
                      ...seasonForm.pricing,
                      baseRate: { ...seasonForm.pricing.baseRate, weekly: e.target.value }
                    }
                  })}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSeasonDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateSeason} 
            variant="contained"
            disabled={!seasonForm.name || !seasonForm.period.startDate || !seasonForm.period.endDate}
          >
            Create Season
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Contract Dialog */}
      <Dialog open={contractDialog} onClose={() => setContractDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Contract</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Autocomplete
              options={guests}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
              value={contractForm.guest}
              onChange={(e, value) => setContractForm({ ...contractForm, guest: value })}
              renderInput={(params) => (
                <TextField {...params} label="Guest" required />
              )}
            />
            
            <Autocomplete
              options={sites.filter(s => s.availability === 'available' || s.availability === 'seasonal')}
              getOptionLabel={(option) => `Site ${option.number} (${option.type})`}
              value={contractForm.site}
              onChange={(e, value) => setContractForm({ ...contractForm, site: value })}
              renderInput={(params) => (
                <TextField {...params} label="Site" required />
              )}
            />
            
            <FormControl fullWidth>
              <InputLabel>Contract Type</InputLabel>
              <Select
                value={contractForm.type}
                onChange={(e) => setContractForm({ ...contractForm, type: e.target.value })}
                label="Contract Type"
              >
                <MenuItem value="seasonal">Seasonal</MenuItem>
                <MenuItem value="annual">Annual</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="weekly_extended">Weekly Extended</MenuItem>
              </Select>
            </FormControl>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <DatePicker
                  label="Start Date"
                  value={contractForm.term.startDate}
                  onChange={(date) => setContractForm({
                    ...contractForm,
                    term: { ...contractForm.term, startDate: date }
                  })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  label="End Date"
                  value={contractForm.term.endDate}
                  onChange={(date) => setContractForm({
                    ...contractForm,
                    term: { ...contractForm.term, endDate: date }
                  })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </Grid>
            
            <TextField
              label="Total Amount"
              type="number"
              value={contractForm.pricing.totalAmount}
              onChange={(e) => setContractForm({
                ...contractForm,
                pricing: { ...contractForm.pricing, totalAmount: e.target.value }
              })}
              fullWidth
              required
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
            />
            
            <FormControl fullWidth>
              <InputLabel>Payment Schedule</InputLabel>
              <Select
                value={contractForm.pricing.paymentSchedule}
                onChange={(e) => setContractForm({
                  ...contractForm,
                  pricing: { ...contractForm.pricing, paymentSchedule: e.target.value }
                })}
                label="Payment Schedule"
              >
                <MenuItem value="lump_sum">Lump Sum</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="semi_annual">Semi-Annual</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContractDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateContract} 
            variant="contained"
            disabled={!contractForm.guest || !contractForm.site || !contractForm.pricing.totalAmount}
          >
            Create Contract
          </Button>
        </DialogActions>
      </Dialog>

      {/* Process Payment Dialog */}
      <Dialog open={paymentDialog} onClose={() => setPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Process Payment</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Payment Amount"
              type="number"
              value={paymentForm.amount}
              onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
              fullWidth
              required
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
            />
            
            <FormControl fullWidth>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={paymentForm.method}
                onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })}
                label="Payment Method"
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="credit">Credit Card</MenuItem>
                <MenuItem value="debit">Debit Card</MenuItem>
                <MenuItem value="check">Check</MenuItem>
                <MenuItem value="ach">ACH Transfer</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Reference/Check Number"
              value={paymentForm.reference}
              onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialog(false)}>Cancel</Button>
          <Button 
            onClick={() => selectedContract && handleProcessPayment(selectedContract._id, 'installment-id')} 
            variant="contained"
            disabled={!paymentForm.amount}
          >
            Process Payment
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SeasonalPage;